// ============================================
// MoltiVerse v3 - Error Tracking System
// ============================================
// Centralized error handling with optional Sentry integration
// To enable Sentry: npm install @sentry/react && add VITE_SENTRY_DSN to .env

import type { ErrorInfo } from 'react';

// ============ CONFIGURATION ============
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN as string | undefined;
const IS_PRODUCTION = import.meta.env.PROD;
const APP_VERSION = '3.0.0';

// ============ ERROR TYPES ============
export type ErrorSeverity = 'fatal' | 'error' | 'warning' | 'info';

export interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  extra?: Record<string, unknown>;
}

// ============ ERROR QUEUE (for offline/batch sending) ============
const errorQueue: Array<{
  error: Error;
  severity: ErrorSeverity;
  context: ErrorContext;
  timestamp: number;
}> = [];

// ============ SENTRY-LIKE INTERFACE ============
interface ErrorTracker {
  captureException: (error: Error, context?: ErrorContext) => void;
  captureMessage: (message: string, severity?: ErrorSeverity) => void;
  setUser: (user: { id: string; username?: string }) => void;
  setTag: (key: string, value: string) => void;
  init: () => void;
}

// ============ MOCK TRACKER (for development) ============
const mockTracker: ErrorTracker = {
  captureException: (error, context) => {
    console.group('🔴 Error Captured');
    console.error('Error:', error);
    if (context) console.log('Context:', context);
    console.log('Stack:', error.stack);
    console.groupEnd();
  },
  captureMessage: (message, severity = 'info') => {
    const emoji = severity === 'fatal' ? '💀' : severity === 'error' ? '❌' : severity === 'warning' ? '⚠️' : 'ℹ️';
    console.log(`${emoji} [${severity.toUpperCase()}] ${message}`);
  },
  setUser: (user) => {
    console.log('📍 User set:', user);
  },
  setTag: (key, value) => {
    console.log(`🏷️ Tag set: ${key}=${value}`);
  },
  init: () => {
    console.log('🔧 Error tracking initialized (development mode)');
  },
};

// ============ PRODUCTION TRACKER ============
// This will integrate with Sentry when configured
let productionTracker: ErrorTracker | null = null;

// Sentry integration is optional - install @sentry/react and set VITE_SENTRY_DSN to enable
async function initSentry(): Promise<ErrorTracker | null> {
  if (!SENTRY_DSN) {
    console.log('[ErrorTracking] No VITE_SENTRY_DSN configured. Using console logging only.');
    return null;
  }

  // Check if @sentry/react is available (optional dependency)
  // This avoids build errors when Sentry isn't installed
  try {
    // For now, we'll just return null since Sentry isn't installed
    // When you want to enable Sentry:
    // 1. npm install @sentry/react
    // 2. Uncomment the dynamic import below
    // 3. Add VITE_SENTRY_DSN to your .env
    
    console.log('[ErrorTracking] Sentry DSN configured but @sentry/react not installed.');
    console.log('[ErrorTracking] To enable: npm install @sentry/react');
    return null;
    
    /* UNCOMMENT WHEN @sentry/react IS INSTALLED:
    const Sentry = await import('@sentry/react');
    
    Sentry.init({
      dsn: SENTRY_DSN,
      environment: IS_PRODUCTION ? 'production' : 'development',
      release: `moltiverse@${APP_VERSION}`,
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
    });

    return {
      captureException: (error, context) => {
        Sentry.captureException(error, {
          tags: {
            component: context?.component,
            action: context?.action,
          },
          extra: context?.extra,
        });
      },
      captureMessage: (message, severity = 'info') => {
        Sentry.captureMessage(message, severity);
      },
      setUser: (user) => {
        Sentry.setUser(user);
      },
      setTag: (key, value) => {
        Sentry.setTag(key, value);
      },
      init: () => {
        console.log('✅ Sentry initialized successfully');
      },
    };
    */
  } catch (err) {
    console.warn('[ErrorTracking] Failed to initialize Sentry:', err);
    return null;
  }
}

// ============ MAIN TRACKER ============
let tracker: ErrorTracker = mockTracker;

/**
 * Initialize error tracking
 * Call this early in app startup
 */
export async function initErrorTracking(): Promise<void> {
  if (IS_PRODUCTION) {
    productionTracker = await initSentry();
    if (productionTracker) {
      tracker = productionTracker;
    }
  }
  tracker.init();
  
  // Flush queued errors
  while (errorQueue.length > 0) {
    const queued = errorQueue.shift();
    if (queued) {
      tracker.captureException(queued.error, queued.context);
    }
  }
}

/**
 * Capture an exception with optional context
 */
export function captureException(error: Error, context?: ErrorContext): void {
  tracker.captureException(error, context);
}

/**
 * Capture a message (for non-exception events)
 */
export function captureMessage(message: string, severity: ErrorSeverity = 'info'): void {
  tracker.captureMessage(message, severity);
}

/**
 * Set current user for error context
 */
export function setUser(user: { id: string; username?: string }): void {
  tracker.setUser(user);
}

/**
 * Set a global tag for all errors
 */
export function setTag(key: string, value: string): void {
  tracker.setTag(key, value);
}

/**
 * Capture React Error Boundary errors
 */
export function captureReactError(error: Error, errorInfo: ErrorInfo, componentName?: string): void {
  captureException(error, {
    component: componentName || 'ErrorBoundary',
    extra: {
      componentStack: errorInfo.componentStack,
    },
  });
}

/**
 * Wrap an async function with error tracking
 */
export function withErrorTracking<T extends (...args: unknown[]) => Promise<unknown>>(
  fn: T,
  context?: ErrorContext
): T {
  return (async (...args: unknown[]) => {
    try {
      return await fn(...args);
    } catch (error) {
      if (error instanceof Error) {
        captureException(error, context);
      }
      throw error;
    }
  }) as T;
}

/**
 * Create a safe wrapper that catches and reports errors
 */
export function safeCall<T>(fn: () => T, fallback: T, context?: ErrorContext): T {
  try {
    return fn();
  } catch (error) {
    if (error instanceof Error) {
      captureException(error, context);
    }
    return fallback;
  }
}
